import axios from "axios";
import { clientLog } from "./clientLog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

// Capture diagnostics without ever exposing authentication tokens to JavaScript storage or URLs.
let networkDegraded = false;
export const setNetworkDegraded = (value) => { networkDegraded = Boolean(value); };

let refreshPromise = null;
async function refreshSession() {
    await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
}

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const original = error.config || {};
        const status = error?.response?.status;
        const url = original.url || "";
        const isAuthFlow = /\/auth\/(login|register|refresh|forgot|reset)/.test(url);

        if (status === 401 && !original._retried && !isAuthFlow) {
            original._retried = true;
            try {
                if (!refreshPromise) {
                    refreshPromise = refreshSession().finally(() => { refreshPromise = null; });
                }
                await refreshPromise;
                return api(original);
            } catch {
                if (typeof window !== "undefined"
                    && !/\/(login|register|reset-password|forgot-password|s\/)/.test(window.location.pathname)) {
                    window.location.assign("/login");
                }
                return Promise.reject(error);
            }
        }

        try {
            if (!url.includes("/monitor/client-log")) {
                const dropped = !error.response;
                if (dropped || status >= 500) {
                    if (dropped && networkDegraded) return Promise.reject(error);
                    const method = (original.method || "get").toUpperCase();
                    const reason = dropped ? (error.code || error.message || "network error") : `HTTP ${status}`;
                    clientLog(`API ${method} ${url} failed: ${reason}`, {
                        status: status || null,
                        code: error.code || null,
                    });
                }
            }
        } catch { /* diagnostics must never break the application */ }
        return Promise.reject(error);
    },
);

export function apiError(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((entry) => (entry && typeof entry.msg === "string" ? entry.msg : JSON.stringify(entry)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export const fileUrl = (signedPath) => {
    if (!signedPath) return "";
    if (/^https?:\/\//i.test(signedPath)) return signedPath;
    if (signedPath.startsWith("/api/")) return `${BACKEND_URL}${signedPath}`;
    return "";
};
